import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TourStep {
  target: string;
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="daily-progress"]',
    title: 'Seu Progresso Diário',
    description: 'Aqui você vê quantos hábitos completou hoje. Mantenha sua streak!',
    position: 'bottom'
  },
  {
    target: '[data-tour="habits-section"]',
    title: 'Seus Hábitos',
    description: 'Faça check-in dos seus hábitos diariamente. Consistência é a chave!',
    position: 'bottom'
  },
  {
    target: '[data-tour="pillars-section"]',
    title: 'Pilares de Foco',
    description: 'Seus pilares organizados. Clique para ver detalhes e adicionar mais hábitos.',
    position: 'top'
  },
  {
    target: '[data-tour="bottom-nav"]',
    title: 'Navegação',
    description: 'Use o menu para explorar todas as funcionalidades: grupos, desafios e seu perfil.',
    position: 'top'
  }
];

export function OnboardingTour() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('has_seen_tour');
    if (!hasSeenTour) {
      // Aguardar renderização completa
      setTimeout(() => setIsOpen(true), 1000);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    localStorage.setItem('has_seen_tour', 'true');
    setIsOpen(false);
  };

  const handleComplete = () => {
    localStorage.setItem('has_seen_tour', 'true');
    setIsOpen(false);
  };

  // Detectar quando o Sheet está aberto e pausar o tour
  useEffect(() => {
    const checkSheetOpen = () => {
      const sheet = document.querySelector('[data-radix-dialog-content]');
      if (sheet && isOpen) {
        setIsPaused(true);
      } else if (!sheet && isPaused) {
        setIsPaused(false);
      }
    };

    const observer = new MutationObserver(checkSheetOpen);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [isOpen, isPaused]);

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        e.preventDefault();
        setCurrentStep(currentStep - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep]);

  useEffect(() => {
    if (!isOpen) return;

    const step = TOUR_STEPS[currentStep];
    const element = document.querySelector(step.target) as HTMLElement;
    
    if (element) {
      setTargetElement(element);
      
      // Scroll suave até o elemento
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Calcular posição do tooltip
      const rect = element.getBoundingClientRect();
      let top = 0;
      let left = 0;

      switch (step.position) {
        case 'bottom':
          top = rect.bottom + 16;
          left = rect.left + rect.width / 2;
          break;
        case 'top':
          top = rect.top - 16;
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 16;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 16;
          break;
      }

      setPosition({ top, left });
      
      // Adicionar destaque ao elemento
      element.classList.add('tour-highlight');
    }

    return () => {
      if (element) {
        element.classList.remove('tour-highlight');
      }
    };
  }, [currentStep, isOpen]);

  if (!isOpen || isPaused) return null;

  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  return (
    <>
      {/* Overlay - não bloqueia cliques */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] animate-fade-in" 
        style={{ pointerEvents: 'none' }}
      />
      
      {/* Tour Card */}
      <Card
        className="fixed z-[10000] w-80 shadow-2xl animate-slide-up"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          transform: step.position === 'bottom' || step.position === 'top' 
            ? 'translateX(-50%)' 
            : 'translateY(-50%)',
          pointerEvents: 'auto'
        }}
      >
        <CardContent className="p-6 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <span className="text-sm font-bold text-primary">
                  {currentStep + 1}
                </span>
              </div>
              <h3 className="font-semibold text-lg">{step.title}</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="h-8 w-8 p-0"
              aria-label="Fechar tour"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <p className="text-sm text-muted-foreground leading-relaxed">
            {step.description}
          </p>

          {/* Keyboard shortcuts hint */}
          <p className="text-xs text-muted-foreground/70">
            💡 Use as setas ← → ou ESC para navegar
          </p>

          {/* Progress */}
          <div className="flex items-center gap-1">
            {TOUR_STEPS.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-1.5 flex-1 rounded-full transition-all duration-300",
                  index <= currentStep ? "bg-primary" : "bg-muted"
                )}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
            >
              Pular tour
            </Button>
            <Button
              onClick={handleNext}
              size="sm"
              className="gap-2"
            >
              {isLastStep ? (
                <>
                  Concluir
                  <CheckCircle2 className="h-4 w-4" />
                </>
              ) : (
                <>
                  Próximo
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tour Highlight Styles */}
      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 9999 !important;
          box-shadow: 0 0 0 4px hsl(var(--primary)), 0 0 0 8px hsl(var(--primary) / 0.3);
          border-radius: 8px;
          animation: pulse 2s infinite;
          pointer-events: auto !important;
          cursor: pointer;
        }

        .tour-highlight * {
          pointer-events: auto !important;
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 4px hsl(var(--primary)), 0 0 0 8px hsl(var(--primary) / 0.3);
          }
          50% {
            box-shadow: 0 0 0 4px hsl(var(--primary)), 0 0 0 12px hsl(var(--primary) / 0.1);
          }
        }
      `}</style>
    </>
  );
}
